import React, { useState } from 'react';
import {
  Box,
  Typography,
  Tab,
  Tabs,
  Card,
  CardContent,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import {
  useDashboardReport,
  usePortfolioAnalysis,
  useLoanPerformance,
  useDailyCollections,
  useExportReport,
} from '../../hooks/reports/useReports';
import { format } from 'date-fns';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const Reports: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [activeTab, setActiveTab] = useState(0);

  // Date range states
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(1); // First day of current month
    return format(date, 'yyyy-MM-dd');
  });
  const [endDate, setEndDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [dailyDate, setDailyDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));

  // Export states
  const [exportFormat, setExportFormat] = useState<'pdf' | 'excel' | 'csv'>('pdf');

  const exportMutation = useExportReport();

  const handleExport = async (reportType: string) => {
    try {
      const exportData: any = {
        reportType,
        format: exportFormat,
      };

      // Add date parameters based on report type
      if (reportType === 'dashboard') {
        // Dashboard doesn't need dates
      } else if (reportType === 'dailyCollections') {
        exportData.date = dailyDate;
      } else {
        exportData.startDate = startDate;
        exportData.endDate = endDate;
      }

      await exportMutation.mutateAsync(exportData);
      enqueueSnackbar(`Report exported as ${exportFormat.toUpperCase()}`, { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Export failed', { variant: 'error' });
    }
  };

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(num);
  };

  const formatPercentage = (value: string | number) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return `${num.toFixed(2)}%`;
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Reports & Analytics
        </Typography>
        <Typography variant="body2" color="text.secondary">
          View comprehensive reports and export data
        </Typography>
      </Box>

      {/* Export Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Export Format:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {(['pdf', 'excel', 'csv'] as const).map((fmt) => (
                <Chip
                  key={fmt}
                  label={fmt.toUpperCase()}
                  onClick={() => setExportFormat(fmt)}
                  color={exportFormat === fmt ? 'primary' : 'default'}
                  variant={exportFormat === fmt ? 'filled' : 'outlined'}
                />
              ))}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label="Dashboard Summary" />
          <Tab label="Portfolio Analysis" />
          <Tab label="Loan Performance" />
          <Tab label="Daily Collections" />
        </Tabs>
      </Box>

      {/* Dashboard Summary */}
      <TabPanel value={activeTab} index={0}>
        <DashboardSummaryTab
          formatCurrency={formatCurrency}
          formatPercentage={formatPercentage}
          onExport={() => handleExport('dashboard')}
          exportFormat={exportFormat}
          isExporting={exportMutation.isPending}
        />
      </TabPanel>

      {/* Portfolio Analysis */}
      <TabPanel value={activeTab} index={1}>
        <PortfolioAnalysisTab
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          formatCurrency={formatCurrency}
          formatPercentage={formatPercentage}
          onExport={() => handleExport('portfolio')}
          exportFormat={exportFormat}
          isExporting={exportMutation.isPending}
        />
      </TabPanel>

      {/* Loan Performance */}
      <TabPanel value={activeTab} index={2}>
        <LoanPerformanceTab
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          formatCurrency={formatCurrency}
          formatPercentage={formatPercentage}
          onExport={() => handleExport('loanPerformance')}
          exportFormat={exportFormat}
          isExporting={exportMutation.isPending}
        />
      </TabPanel>

      {/* Daily Collections */}
      <TabPanel value={activeTab} index={3}>
        <DailyCollectionsTab
          date={dailyDate}
          onDateChange={setDailyDate}
          formatCurrency={formatCurrency}
          onExport={() => handleExport('dailyCollections')}
          exportFormat={exportFormat}
          isExporting={exportMutation.isPending}
        />
      </TabPanel>
    </Box>
  );
};

// Dashboard Summary Tab Component
function DashboardSummaryTab({ formatCurrency, formatPercentage, onExport, exportFormat, isExporting }: any) {
  const { data, isLoading, error, refetch } = useDashboardReport();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load dashboard report. {error instanceof Error ? error.message : 'Please try again.'}
      </Alert>
    );
  }

  if (!data || !data.dashboard) {
    return (
      <Alert severity="warning">
        No dashboard data available. Please try refreshing.
      </Alert>
    );
  }

  const { dashboard } = data;

  return (
    <Box>
      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, justifyContent: 'flex-end' }}>
        <Button startIcon={<RefreshIcon />} onClick={() => refetch()}>
          Refresh
        </Button>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={onExport}
          disabled={isExporting}
        >
          {isExporting ? 'Exporting...' : `Export ${exportFormat.toUpperCase()}`}
        </Button>
      </Box>

      {/* Metrics Grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 2, mb: 3 }}>
        <MetricCard
          title="Total Customers"
          value={dashboard.customerMetrics.totalCustomers}
          subtitle={`${dashboard.customerMetrics.activeCustomers} active`}
          color="primary"
        />
        <MetricCard
          title="Active Loans"
          value={dashboard.loanMetrics.totalActiveLoans}
          subtitle={formatCurrency(dashboard.loanMetrics.totalDisbursed)}
          color="info"
        />
        <MetricCard
          title="Portfolio at Risk (PAR30)"
          value={formatPercentage(dashboard.portfolioQuality.par30)}
          subtitle={parseFloat(dashboard.portfolioQuality.par30) < 10 ? 'Good' : 'Needs Attention'}
          color={parseFloat(dashboard.portfolioQuality.par30) < 10 ? 'success' : 'warning'}
        />
        <MetricCard
          title="Total Revenue"
          value={formatCurrency(dashboard.revenueMetrics.totalRevenue)}
          subtitle="All time"
          color="success"
        />
      </Box>

      {/* Detailed Sections */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        {/* Applications */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Applications
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <MetricRow label="Total" value={dashboard.applicationMetrics.totalApplications} />
              <MetricRow label="Pending" value={dashboard.applicationMetrics.pendingApplications} />
              <MetricRow label="Approved" value={dashboard.applicationMetrics.approvedApplications} />
              <MetricRow label="Rejected" value={dashboard.applicationMetrics.rejectedApplications} />
            </Box>
          </CardContent>
        </Card>

        {/* Collections */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Collections
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <MetricRow label="Today" value={formatCurrency(dashboard.collectionMetrics.collectedToday)} />
              <MetricRow label="This Week" value={formatCurrency(dashboard.collectionMetrics.collectedThisWeek)} />
              <MetricRow label="This Month" value={formatCurrency(dashboard.collectionMetrics.collectedThisMonth)} />
              <MetricRow label="Total" value={formatCurrency(dashboard.collectionMetrics.totalCollected)} />
            </Box>
          </CardContent>
        </Card>

        {/* Portfolio Quality */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Portfolio Quality
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <MetricRow label="PAR 30" value={formatPercentage(dashboard.portfolioQuality.par30)} />
              <MetricRow label="NPL Rate" value={formatPercentage(dashboard.portfolioQuality.npl)} />
              <MetricRow label="Collection Rate" value={formatPercentage(dashboard.portfolioQuality.collectionRate)} />
            </Box>
          </CardContent>
        </Card>

        {/* Revenue Breakdown */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Revenue Breakdown
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <MetricRow label="Interest Income" value={formatCurrency(dashboard.revenueMetrics.interestIncome)} />
              <MetricRow label="Processing Fees" value={formatCurrency(dashboard.revenueMetrics.processingFeeIncome)} />
              <MetricRow label="Penalties" value={formatCurrency(dashboard.revenueMetrics.penaltyIncome)} />
              <MetricRow label="Total Revenue" value={formatCurrency(dashboard.revenueMetrics.totalRevenue)} bold />
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

// Portfolio Analysis Tab Component
function PortfolioAnalysisTab({ startDate, endDate, onStartDateChange, onEndDateChange, formatCurrency, formatPercentage, onExport, exportFormat, isExporting }: any) {
  const { data, isLoading, error, refetch } = usePortfolioAnalysis({ startDate, endDate });

  return (
    <Box>
      {/* Date Range Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => onStartDateChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => onEndDateChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <Button startIcon={<RefreshIcon />} onClick={() => refetch()} size="small">
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={onExport}
              disabled={isExporting}
              size="small"
            >
              {isExporting ? 'Exporting...' : `Export ${exportFormat.toUpperCase()}`}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error">
          Failed to load portfolio analysis. {error instanceof Error ? error.message : 'Please try again.'}
        </Alert>
      )}

      {data && data.analysis && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          {/* Portfolio Size */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Portfolio Size
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <MetricRow label="Total Loans" value={data.analysis.portfolioSize.totalLoans} />
                <MetricRow label="Total Disbursed" value={formatCurrency(data.analysis.portfolioSize.totalDisbursed)} />
                <MetricRow label="Average Loan Size" value={formatCurrency(data.analysis.portfolioSize.averageLoanSize)} />
              </Box>
            </CardContent>
          </Card>

          {/* Repayment Performance */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Repayment Performance
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <MetricRow label="Total Repaid" value={formatCurrency(data.analysis.repaymentPerformance.totalRepaid)} />
                <MetricRow label="On-Time Repayments" value={data.analysis.repaymentPerformance.onTimeRepayments} />
                <MetricRow label="Late Repayments" value={data.analysis.repaymentPerformance.lateRepayments} />
              </Box>
            </CardContent>
          </Card>

          {/* Portfolio at Risk */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Portfolio at Risk
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <MetricRow label="PAR 1 Day" value={formatPercentage(data.analysis.portfolioAtRisk.par1)} />
                <MetricRow label="PAR 7 Days" value={formatPercentage(data.analysis.portfolioAtRisk.par7)} />
                <MetricRow label="PAR 30 Days" value={formatPercentage(data.analysis.portfolioAtRisk.par30)} />
              </Box>
            </CardContent>
          </Card>

          {/* Loan Status Breakdown */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Loan Status Breakdown
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <MetricRow label="Active Loans" value={data.analysis.loanStatusBreakdown.activeLoans} />
                <MetricRow label="Paid Loans" value={data.analysis.loanStatusBreakdown.paidLoans} />
                <MetricRow label="Defaulted Loans" value={data.analysis.loanStatusBreakdown.defaultedLoans} />
              </Box>
            </CardContent>
          </Card>

          {/* Customer Behavior */}
          <Card sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Customer Behavior
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <MetricRow label="Repeat Borrowers" value={data.analysis.customerBehavior.repeatBorrowers} />
                <MetricRow label="First-Time Borrowers" value={data.analysis.customerBehavior.firstTimeBorrowers} />
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
}

// Loan Performance Tab Component
function LoanPerformanceTab({ startDate, endDate, onStartDateChange, onEndDateChange, formatCurrency, formatPercentage, onExport, exportFormat, isExporting }: any) {
  const { data, isLoading, error, refetch } = useLoanPerformance({ startDate, endDate });

  return (
    <Box>
      {/* Date Range Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => onStartDateChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => onEndDateChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <Button startIcon={<RefreshIcon />} onClick={() => refetch()} size="small">
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={onExport}
              disabled={isExporting}
              size="small"
            >
              {isExporting ? 'Exporting...' : `Export ${exportFormat.toUpperCase()}`}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error">
          Failed to load loan performance. {error instanceof Error ? error.message : 'Please try again.'}
        </Alert>
      )}

      {data && data.report && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          {/* Disbursement Performance */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Disbursement Performance
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <MetricRow label="Total Disbursed" value={formatCurrency(data.report.disbursementPerformance.totalDisbursed)} />
                <MetricRow label="Number of Loans" value={data.report.disbursementPerformance.numberOfLoans} />
                <MetricRow label="Average Loan Size" value={formatCurrency(data.report.disbursementPerformance.averageLoanSize)} />
              </Box>
            </CardContent>
          </Card>

          {/* Repayment Performance */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Repayment Performance
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <MetricRow label="Total Repaid" value={formatCurrency(data.report.repaymentPerformance.totalRepaid)} />
                <MetricRow label="Fully Paid Loans" value={data.report.repaymentPerformance.fullyPaidLoans} />
                <MetricRow label="Partially Paid Loans" value={data.report.repaymentPerformance.partiallyPaidLoans} />
              </Box>
            </CardContent>
          </Card>

          {/* Default Analysis */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Default Analysis
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <MetricRow label="Defaulted Loans" value={data.report.defaultAnalysis.defaultedLoans} />
                <MetricRow label="Default Rate" value={formatPercentage(data.report.defaultAnalysis.defaultRate)} />
              </Box>
            </CardContent>
          </Card>

          {/* Profitability */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Profitability
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <MetricRow label="Interest Earned" value={formatCurrency(data.report.profitability.interestEarned)} />
                <MetricRow label="Fees Earned" value={formatCurrency(data.report.profitability.feesEarned)} />
                <MetricRow label="Penalties Earned" value={formatCurrency(data.report.profitability.penaltiesEarned)} />
                <MetricRow label="Total Earned" value={formatCurrency(data.report.profitability.totalEarned)} bold />
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
}

// Daily Collections Tab Component
function DailyCollectionsTab({ date, onDateChange, formatCurrency, onExport, exportFormat, isExporting }: any) {
  const { data, isLoading, error, refetch } = useDailyCollections({ date });

  return (
    <Box>
      {/* Date Control */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              label="Report Date"
              type="date"
              value={date}
              onChange={(e) => onDateChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <Button startIcon={<RefreshIcon />} onClick={() => refetch()} size="small">
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={onExport}
              disabled={isExporting}
              size="small"
            >
              {isExporting ? 'Exporting...' : `Export ${exportFormat.toUpperCase()}`}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error">
          Failed to load daily collections. {error instanceof Error ? error.message : 'Please try again.'}
        </Alert>
      )}

      {data && data.report && (
        <>
          {/* Summary Cards */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Collections Summary
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <MetricRow label="Total Collected" value={formatCurrency(data.report.collectionsSummary.totalCollected)} />
                  <MetricRow label="Number of Payments" value={data.report.collectionsSummary.numberOfPayments} />
                  <MetricRow label="Average Payment" value={formatCurrency(data.report.collectionsSummary.averagePaymentSize)} />
                </Box>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Due Today
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <MetricRow label="Loans Due Today" value={data.report.dueToday.loansDueToday} />
                  <MetricRow label="Amount Due Today" value={formatCurrency(data.report.dueToday.amountDueToday)} />
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Overdue Breakdown */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Overdue Breakdown
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    1-7 Days Overdue
                  </Typography>
                  <Typography variant="h6">{data.report.overdueBreakdown.overdue1_7Days.count} loans</Typography>
                  <Typography variant="body2">{formatCurrency(data.report.overdueBreakdown.overdue1_7Days.amount)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    8-29 Days Overdue
                  </Typography>
                  <Typography variant="h6">{data.report.overdueBreakdown.overdue8_29Days.count} loans</Typography>
                  <Typography variant="body2">{formatCurrency(data.report.overdueBreakdown.overdue8_29Days.amount)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    30+ Days Overdue
                  </Typography>
                  <Typography variant="h6">{data.report.overdueBreakdown.overdue30PlusDays.count} loans</Typography>
                  <Typography variant="body2" color="error">{formatCurrency(data.report.overdueBreakdown.overdue30PlusDays.amount)}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Payment Details Table */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Payment Details
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Loan Number</strong></TableCell>
                      <TableCell><strong>Customer</strong></TableCell>
                      <TableCell><strong>Amount</strong></TableCell>
                      <TableCell><strong>Method</strong></TableCell>
                      <TableCell><strong>Reference</strong></TableCell>
                      <TableCell><strong>Time</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.report.paymentDetails.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography color="text.secondary">No payments recorded for this date</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      data.report.paymentDetails.map((payment) => (
                        <TableRow key={payment.repaymentId}>
                          <TableCell>{payment.loanNumber}</TableCell>
                          <TableCell>{payment.customerName}</TableCell>
                          <TableCell>{formatCurrency(payment.amount)}</TableCell>
                          <TableCell>
                            <Chip label={payment.paymentMethod} size="small" />
                          </TableCell>
                          <TableCell>{payment.paymentReference}</TableCell>
                          <TableCell>{format(new Date(payment.paymentDate), 'HH:mm')}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}

// Helper Components
function MetricCard({ title, value, subtitle, color = 'default' }: any) {
  return (
    <Card>
      <CardContent>
        <Typography variant="caption" color="text.secondary" gutterBottom display="block">
          {title}
        </Typography>
        <Typography variant="h4" fontWeight="bold" color={`${color}.main`}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function MetricRow({ label, value, bold = false }: any) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={bold ? 'bold' : 'regular'}>
        {value}
      </Typography>
    </Box>
  );
}

export default Reports;
